import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react'
import { Table, Space, Popconfirm, Input } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { User } from 'types/users'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

type Props = {
    data: User[]
    onEdit: (id: number) => void
    onDelete: (id: number) => void
    onBlock: (id: number) => void
    onActivate: (id: number) => void
    onSetUserPassword: (id: number, password: string) => void
    onShowRoles: (id: number) => void
    loading: boolean
}

export const UsersTable = memo((props: Props) => {
    const { data, onEdit, onDelete, loading, onBlock, onActivate, onSetUserPassword, onShowRoles } = props
    const [password, setPassword] = useState<string>('')

    const changePassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
    }, [])

    const handleSetPassword = useCallback(
        (id: number) => {
            onSetUserPassword(id, password)
        },
        [onSetUserPassword, password]
    )

    const handleCancelSetPassword = useCallback(() => {
        setPassword('')
    }, [])

    const columns: ColumnsType<User> = useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: 'E-mail',
                dataIndex: 'email',
                key: 'email',
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, record) => (record.status === 1 ? 'Active' : record.status === 0 ? 'Blocked' : 'Undefined'),
            },
            {
                title: 'Password',
                key: 'hash',
                render: (_, record) => (record.hash ? 'Has' : 'Not has'),
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Space size='middle'>
                        <a onClick={() => onShowRoles(record.id)}>Roles</a>
                        <a onClick={() => onEdit(record.id)}>Edit</a>
                        <Popconfirm
                            title={formatModalTitle('Delete user', record.email)}
                            description={'Are you sure to delete this user?'}
                            onConfirm={() => onDelete(record.id)}
                            okText={'Yes'}
                            cancelText={'No'}
                        >
                            <a href='#'>Delete</a>
                        </Popconfirm>
                        {record.status === 1 ? (
                            <a onClick={() => onBlock(record.id)}>Block</a>
                        ) : record.status === 0 ? (
                            <a onClick={() => onActivate(record.id)}>Activate</a>
                        ) : null}
                        <Popconfirm
                            title={formatModalTitle('Set password', record.email)}
                            description={<Input value={password} onChange={changePassword} />}
                            onConfirm={() => handleSetPassword(record.id)}
                            onCancel={handleCancelSetPassword}
                            okText={'Yes'}
                            cancelText={'No'}
                        >
                            <a href='#'>Set password</a>
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [
            changePassword,
            handleCancelSetPassword,
            handleSetPassword,
            onActivate,
            onBlock,
            onDelete,
            onEdit,
            onShowRoles,
            password,
        ]
    )

    return <Table loading={loading} columns={columns} dataSource={data} rowKey='id' pagination={false} />
})
